import { useSession } from 'next-auth/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { createEditor } from 'slate';
import { Slate, withReact } from 'slate-react';
import styled from 'styled-components';

import { ArrowDownSvg } from '../../../assets/icons';
import BrainSvg from '../../../assets/icons/BrainSvg';
import FoldedHandsSVG from '../../../assets/icons/FoldedHands';
import { RefreshSvg } from '../../../assets/icons/RefreshSvg';
import theme from '../../../assets/theme';
import GameAPI from '../../../frontendServices/gameAPI';
import useAccessBadge from '../../../hooks/useAccessBadge';
import useConsumeGenericGeneratorAsNewPresentation from '../../../hooks/useConsumeGenericGeneratorAsNewPresentation';
import { useMetrics } from '../../../hooks/useMetrics';
import useRouterMatch from '../../../hooks/useRouterMatch';
import SocketProvider from '../../../providers/SocketProvider/SocketProvider';
import { markdownToSlateEnhancedLearningObjective } from '../../../utils/richTextUtils';
import { Loader } from '../../_atoms/_Loader';
import { TextArea } from '../../_atoms/_TextArea';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { LoadingDots } from '../../_atoms/StreamingLongAnswerList/StreamingLongAnswerList';
import BaseModal from '../../_molecules/BaseModal';
import ShowLessonCreationProgressBar from '../../_molecules/ProgressBar/ShowLessonCreationProgressBar';
import { Element, Leaf, SlateEditable } from '../../SlideComponents/slateUtil';
import { CenteredContainer } from '../../styled/CenteredContainer';
import { useLearningObjectiveRefinement } from './hooks/useLearningObjectiveRefinementHook';

const isSlate = (text: string) => {
  return text.startsWith('[{') && text.endsWith('}]');
};

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const LessonReadyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
  padding: 20px;
  background-color: ${theme.colors.white};
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  border-radius: 12px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
`;

const LessonReadyMessage = styled.div`
  text-align: center;
  color: ${theme.colors.black};
  font-size: 16px;
  line-height: 1.5;
  font-weight: 600;
`;

const LessonReadyButtons = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const GenerateLessonButton = styled(Button)`
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background-color: ${theme.colors.orange};
  color: ${theme.colors.white};

  &:hover {
    background-color: ${theme.colors.orangeDark};
  }

  &:active {
    transform: translateY(1px);
  }
`;

const ChatMessageViewer = ({ content }: { content: string }) => {
  const metrics = useMetrics();
  const editor = useMemo(() => withReact(createEditor()), []);
  console.log('this is the content', content);
  const parsedContent = useMemo(
    () =>
      isSlate(content)
        ? JSON.parse(content)
        : markdownToSlateEnhancedLearningObjective(content, 16),
    [content],
  );
  console.log('this is the parsed content', parsedContent);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderElement = React.useCallback((props: any) => {
    // Ensure all elements have a minimum font size of 16px
    const elementWithSize = {
      ...props.element,
      size: 16,
    };
    return <Element {...props} element={elementWithSize} />;
  }, []);
  const renderLeaf = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (props: any) => {
      return <Leaf {...props} metrics={metrics} />;
    },
    [metrics],
  );

  useEffect(() => {
    editor.children = parsedContent;
    editor.onChange();
  }, [parsedContent, editor]);

  return (
    <SlateWrapper>
      <span translate="no">
        <Slate editor={editor} value={parsedContent}>
          <SlateEditable
            readOnly={true}
            $readOnly={true}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            style={{
              fontSize: 16,
              backgroundColor: 'transparent',
              padding: 0,
              margin: 0,
              border: 'none',
              boxShadow: 'none',
            }}
          />
        </Slate>
      </span>
    </SlateWrapper>
  );
};

const SlateWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  ul,
  ol {
    padding-inline-start: 10px;
  }
`;

const lessonSuggestions = [
  {
    summary: 'Making inferences',
    fullValue:
      '6th grade novel study on The Giver Chapter 3 focusing on making inferences.',
  },
  {
    summary: 'Sensory imagery',
    fullValue:
      '4th grade writing lesson. Use sensory imagery to convey a sequence of events from summer break.',
  },
  {
    summary: 'Counterclaims',
    fullValue:
      '9th grade ELA lesson on developing counterclaims. Focus on anticipating views of the intended audience. The topic should be high school electives.',
  },
  {
    summary: 'Two-step word problems',
    fullValue:
      '2nd grade math lesson on using subtraction within 100 to solve two-step word problems. Include problems about the playground.',
  },
  {
    summary: 'Proportions',
    fullValue:
      '7th grade math lesson on using proportions to solve real-world percent problems.',
  },
  {
    summary: 'Food webs',
    fullValue:
      '5th grade science lesson about the movement of matter between producers, consumers, and decomposers in food webs in the Everglades National Park.',
  },
  {
    summary: 'Photosynthesis',
    fullValue:
      '8th grade science lesson on photosynthesis focusing on the structure of the chloroplast.',
  },
  {
    summary: 'The American Revolution',
    fullValue:
      '10th grade U.S. history lesson. How did protests escalate tensions between Britain and the colonies leading up to the American Revolution?',
  },
  {
    summary: 'Early Hominids',
    fullValue:
      '6th grade social studies lesson on the capabilities that helped *Homo habilis* ("Handy Man") survive.',
  },
  {
    summary: 'Developing Empathy',
    fullValue:
      '3rd grade SEL lesson about recognizing that others may have different perspectives and experiences.',
  },
  {
    summary: 'Digital Citizenship',
    fullValue:
      '8th grade digital citizenship lesson. How do digital media tools try to hook you, and what can you do about it?',
  },
  {
    summary: 'CTE',
    fullValue:
      '11th grade CTE lesson on how to respond to commonly used interview questions for a job in hospitality.',
  },
];

const ObjectiveRefinementWithSocket = ({ templateId }: { templateId: string }) => {
  const [inputValue, setInputValue] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const streamCleanupRef = useRef<(() => void) | null>(null);
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const tenantSubjects = userAccessBadge?.user.profileInfo?.subjects;
  const tenantGrades = userAccessBadge?.user.profileInfo?.grades;
  const presetState = userAccessBadge?.organization?.region;
  const [showLoader, setShowLoader] = useState(false);
  const metrics = useMetrics();
  const {
    isStreaming,
    streamData,
    startStream,
    resetStream,
    error,
    stopStream,
    isObjectiveReady,
    promptVariables,
    sessionLength,
    setIsObjectiveReady,
  } = useLearningObjectiveRefinement();

  const { tenantId, router } = useRouterMatch();

  const {
    consumeGenerator,
    isLoading: isGeneratingLesson,
    isError,
    data,
    error: errorConsumingGenerator,
  } = useConsumeGenericGeneratorAsNewPresentation(tenantId);

  useEffect(() => {
    if (isGeneratingLesson || data) {
      setShowLoader(true);
      return;
    }
    {
      setShowLoader(false);
      return;
    }
  }, [data, isGeneratingLesson, setShowLoader]);

  useEffect(() => {
    if (data) {
      const blockLabels = (data.content.slides || [])
        .filter((value, index, self) => self.indexOf(value) === index)
        .map(slide => slide.blockId?.split('-')[0])
        .filter(Boolean);

      metrics.logEvent('CreatePage.Generator.Consume.FullLessonSuccessRedirect', {
        generatorId: 'genericFullLesson',
        generatorName: 'genericFullLesson',
        standard: promptVariables?.standardAlignment,
        subject: promptVariables?.subject,
        grade: promptVariables?.grade,
        generatedLessonId: data.id,
        tenantSubjects: tenantSubjects,
        language: promptVariables?.languages?.[0] || 'English',
        tenantGrades: tenantGrades,
        schoolState: presetState,
        prompt: promptVariables?.standardAlignment
          ? `${promptVariables.learningObjective} ${promptVariables.standardAlignment}`
          : promptVariables?.learningObjective,
        skeleton: data.metadata?.skeleton,
        blocks: blockLabels,
        sessionLength: sessionLength,
      });

      router.push(`/${tenantId}/lessons/${data.id}/edit`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, tenantId, data]);

  // Randomly select 3 suggestions
  const [randomSuggestions, setRandomSuggestions] = useState(() => {
    const shuffled = [...lessonSuggestions].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3);
  });

  const shuffleSuggestions = () => {
    const shuffled = [...lessonSuggestions].sort(() => 0.5 - Math.random());
    setRandomSuggestions(shuffled.slice(0, 3));
  };

  // Auto scroll to bottom when chat history changes
  useEffect(() => {
    if (chatScrollRef.current) {
      const scrollToBottom = () => {
        chatScrollRef.current!.scrollTop = chatScrollRef.current!.scrollHeight;
      };

      // Scroll immediately
      scrollToBottom();

      // Also scroll after a short delay to ensure content is rendered
      const timeoutId = setTimeout(scrollToBottom, 1);

      return () => clearTimeout(timeoutId);
    }
  }, [chatHistory, streamData.currentContent]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamCleanupRef.current) {
        streamCleanupRef.current();
      }
    };
  }, []);

  // Reset stream when complete and add to chat history
  useEffect(() => {
    if (streamData.isComplete) {
      // Capture the content before resetting
      const completedContent = streamData.currentContent;

      // Add the completed assistant message to chat history
      if (completedContent) {
        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content: completedContent,
          timestamp: new Date(),
        };
        setChatHistory(prev => [...prev, assistantMessage]);
      }

      // Small delay to ensure content is saved before resetting
      setTimeout(() => {
        resetStream();
      }, 100);
    }
  }, [streamData.isComplete, resetStream, streamData.currentContent]);

  // Handle successful lesson generation
  useEffect(() => {
    if (data) {
      router.push(`/${tenantId}/lessons/${data.id}/edit`);
    }
  }, [data, router, tenantId]);

  const handleSuggestionClick = (fullValue: string) => {
    setInputValue(fullValue);
  };

  const handleSubmit = async () => {
    if (!inputValue.trim()) return;

    // Add user message to chat history immediately
    const userMessage: ChatMessage = {
      role: 'user',
      content: inputValue,
      timestamp: new Date(),
    };
    setChatHistory(prev => [...prev, userMessage]);

    // Start the stream with just the message
    const cleanup = startStream(inputValue);
    streamCleanupRef.current = cleanup || null;

    setInputValue('');
  };

  useEffect(() => {
    if (isError) {
      setShowErrorModal(true);
    }
  }, [isError]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleGenerateLesson = () => {
    if (!promptVariables) {
      console.error('No prompt variables available');
      return;
    }

    const prompt = promptVariables.standardAlignment
      ? `${promptVariables.learningObjective} ${promptVariables.standardAlignment}`
      : promptVariables.learningObjective;

    // Use the consumeGenerator similar to AnyContentOrSkillTab
    consumeGenerator({
      prompt: prompt,
      grade: promptVariables.grade,
      subject: promptVariables.subject || 'otherSubjects', // Default fallback
      lessonCategory: promptVariables.lessonCategory,
      languages: promptVariables.languages || ['English'], // Default fallback
    });
  };

  return (
    <Container>
      {/* Show loader when generating lesson */}
      {showLoader && (
        <LoaderContainer>
          <ShowLessonCreationProgressBar />
        </LoaderContainer>
      )}
      <BaseModal visible={showErrorModal} onCancel={() => setShowErrorModal(false)}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <IconWrapper height="120px" width="120px" style={{ marginTop: 20 }}>
            <FoldedHandsSVG />
          </IconWrapper>

          <ErrorMessage>
            <>
              <ErrorMessage style={{ fontWeight: 800 }}>
                Uh-oh! Something went wrong on our end
              </ErrorMessage>

              <ErrorMessage style={{ fontSize: '16px' }}>
                {errorConsumingGenerator?.customMessage ||
                  'Please try again or contact support if the issue persists'}
              </ErrorMessage>
            </>
          </ErrorMessage>
          <Button
            styles={{ marginTop: 20 }}
            width="120px"
            onClick={() => setShowErrorModal(false)}
          >
            Ok
          </Button>
        </div>
      </BaseModal>
      {!showLoader && (
        <>
          <Title>What will you teach today?</Title>
          <Subtitle>
            Make an interactive lesson students love by chatting with Curipod AI
          </Subtitle>

          {chatHistory.length >= 1 && (
            <ChatContainer>
              <ChatHistory ref={chatScrollRef}>
                {chatHistory.map((message, index) => (
                  <ChatMessage key={index} role={message.role}>
                    <ChatMessageViewer content={message.content} />
                  </ChatMessage>
                ))}

                {streamData.currentContent && (
                  <ChatMessage role="assistant">
                    <ChatMessageViewer content={streamData.currentContent} />
                  </ChatMessage>
                )}

                {isStreaming && streamData.state && (
                  <ChatMessage role="assistant">
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '20px auto',
                        gap: 5,
                        alignItems: 'center',
                      }}
                    >
                      <IconWrapper height="16px" width="16px">
                        <BrainSvg />
                      </IconWrapper>
                      <ThinkingMessage>{streamData.state}</ThinkingMessage>
                    </div>
                  </ChatMessage>
                )}

                {/* Show Generate Lesson options when objective is ready */}
                {isObjectiveReady && !!promptVariables && (
                  <LessonReadyContainer>
                    <LessonReadyMessage>
                      Review the learning objective if everything looks good hit
                      &quot;Generate lesson&quot;
                      <br />
                      You can also make revisions by chatting with me.
                    </LessonReadyMessage>
                    <LessonReadyButtons>
                      <Button
                        type="ghost"
                        onClick={() => {
                          // Focus back on the input to continue chatting
                          if (inputRef.current) {
                            setIsObjectiveReady(false);
                            inputRef.current.focus();
                          }
                        }}
                      >
                        Keep chatting
                      </Button>
                      <GenerateLessonButton
                        onClick={handleGenerateLesson}
                        loading={isGeneratingLesson}
                        disabled={isGeneratingLesson}
                      >
                        Generate lesson
                      </GenerateLessonButton>
                    </LessonReadyButtons>
                  </LessonReadyContainer>
                )}
              </ChatHistory>
            </ChatContainer>
          )}
          <StretchyContainer
            style={{ maxHeight: chatHistory.length >= 1 ? 'fit-content' : 'unset' }}
          >
            <InputContainer>
              <TextArea
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Describe your lesson - grade, subject and topic"
                rows={2}
                ref={inputRef}
                disabled={isStreaming || !!error}
                style={{
                  minWidth: '100%',
                }}
                textAreaStyled={{
                  fontSize: 16,
                  fontWeight: 600,
                  padding: '10px 40px 10px 20px',
                  borderRadius: 10,
                  boxShadow: theme.effects.floatingCreatorElementShadow,
                }}
              />
              {!isStreaming && !error && (
                <SubmitButton
                  onClick={handleSubmit}
                  disabled={!inputValue.trim() || isStreaming}
                >
                  <IconWrapper
                    style={{ transform: 'rotate(-90deg)' }}
                    height="20px"
                    width="20px"
                    color={theme.colors.white}
                  >
                    <ArrowDownSvg />
                  </IconWrapper>
                </SubmitButton>
              )}
              {isStreaming && (
                <GeneratingContainer>
                  <GeneratingActionContainer>
                    <span>
                      Generating<LoadingDots>...</LoadingDots>
                    </span>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        justifyContent: 'flex-end',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        stopStream();
                      }}
                    >
                      <span>Stop</span>
                      <IconWrapper
                        height="14px"
                        width="14px"
                        color={theme.colors.greyDark}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 14 14"
                          fill="currentColor"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12.6 0H1.4C0.6286 0 0 0.6286 0 1.4V12.6C0 13.3714 0.6286 14 1.4 14H12.6C13.3714 14 14 13.3714 14 12.6V1.4C14 0.6286 13.3714 0 12.6 0Z"
                            fill="currentColor"
                          />
                        </svg>
                      </IconWrapper>
                    </div>
                  </GeneratingActionContainer>
                </GeneratingContainer>
              )}
            </InputContainer>

            {chatHistory.length === 0 && !error && (
              <SuggestionsWrapper>
                <SuggestionsContainer>
                  <ShuffleButton onClick={shuffleSuggestions} title="Get new suggestions">
                    <IconWrapper height="16px" width="16px" color={theme.colors.greyDark}>
                      <RefreshSvg />
                    </IconWrapper>
                  </ShuffleButton>
                  {randomSuggestions.map((suggestion, index) => (
                    <SuggestionButton
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion.fullValue)}
                    >
                      {suggestion.summary}
                    </SuggestionButton>
                  ))}
                </SuggestionsContainer>
              </SuggestionsWrapper>
            )}
          </StretchyContainer>
        </>
      )}
    </Container>
  );
};

const ErrorMessage = styled.h4`
  font-weight: bold;
  width: 100%;
  text-align: center;
  margin-top: 20px;
  font-size: 18px;

  p {
    margin-top: 10px;
    font-size: 14px;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 800;
  color: ${theme.colors.black};
  margin-bottom: 10px;

  @media (max-width: 700px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.h2`
  font-size: 20px;

  @media (max-width: 700px) {
    font-size: 16px;
    margin-bottom: 30px;
  }
`;

const ChatContainer = styled.div`
  width: 100%;
  flex: 1;
  margin-bottom: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ChatHistory = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px;
  min-height: 0;
`;

const ChatMessage = styled.div<{ role: 'user' | 'assistant' }>`
  padding: 12px;
  border-radius: 8px;
  max-width: 95%;
  align-self: ${props => (props.role === 'user' ? 'flex-end' : 'flex-start')};
  background-color: ${props =>
    props.role === 'user' ? theme.colors.paleBeige : theme.colors.translucentWhite};
  color: ${props => (props.role === 'user' ? theme.colors.black : theme.colors.black)};
  font-size: 16px;
  font-weight: 600;
`;

const InputContainer = styled.div`
  width: 100%;
  margin-bottom: 30px;
  position: relative;
`;

const StretchyContainer = styled.div`
  width: 100%;
  margin-bottom: 30px;
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const SubmitButton = styled.button`
  position: absolute;
  right: 5px;
  bottom: 5px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${theme.colors.green};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${theme.colors.lightGreen};
    transform: scale(1.05);
  }

  &:disabled {
    background-color: ${theme.colors.greyLight};
    cursor: not-allowed;
  }
`;

const GeneratingContainer = styled.div`
  position: absolute;
  width: 95%;
  height: 30px;
  left: 0;
  bottom: 0px;
  padding: 0px 10px;
`;

const GeneratingActionContainer = styled.div`
  display: grid;
  grid-template-columns: auto 100px;
  border-top: 1px solid #d9d9d9;
  align-items: center;
  padding: 1px 15px;
  height: 100%;
`;

const ThinkingMessage = styled.p`
  text-transform: capitalize;
  display: flex;
  align-items: center;
  margin-bottom: 0;
  gap: 8px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  background: linear-gradient(
    to left,
    #b9b9b9 20%,
    #393939 30%,
    #393939 70%,
    #b9b9b9 80%
  );
  background-clip: text;
  -webkit-text-fill-color: transparent;
  text-fill-color: transparent;
  background-size: 200% auto;
  animation: textShine 2.5s linear infinite;
  @keyframes textShine {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: -100% 50%;
    }
  }
`;

const SuggestionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const SuggestionsContainer = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
`;

const SuggestionButton = styled.button`
  font-size: 14px;
  color: ${theme.colors.greyVeryDark};
  padding: 8px 16px;
  border: 1px solid ${theme.colors.greyLight};
  border-radius: 20px;
  background-color: white;
  transition: all 0.2s ease;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: ${theme.colors.greyLight};
    border-color: ${theme.colors.greyDark};
  }
`;

const ShuffleButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${theme.colors.greyLight};
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.greyLight};
    border-color: ${theme.colors.greyDark};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const LoaderContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const ObjectiveRefinement = ({ templateId }: { templateId: string }) => {
  const session = useSession();
  const gameAPI = React.useMemo(() => {
    if (session.data?.idToken) {
      return new GameAPI(session.data.idToken as string);
    }
    return undefined;
  }, [session.data?.idToken]);

  if (session.status === 'loading') {
    return <div>Loading...</div>;
  }

  if (session.status === 'unauthenticated') {
    return <div>Please sign in to access this feature.</div>;
  }

  if (!gameAPI) {
    return (
      <CenteredContainer>
        <Loader />
      </CenteredContainer>
    );
  }

  return (
    <SocketProvider gameAPI={gameAPI}>
      <ObjectiveRefinementWithSocket templateId={templateId} />
    </SocketProvider>
  );
};

export default ObjectiveRefinement;
